// What every piece of chart furniture needs and no piece states twice
// (R548, ui-map-interface.md §17).
//
// These things repeat in the piece modules: the ink of a weight, the ink a
// reading asked for, the face of a reading, the clock forms the cards
// write, the grid lines of the sheet, the cull test of a mark, the dashed
// ring, the dashed loop, the dashed outline, the show flag, the loop a name
// rides and the crown of a loop. They are gathered here so a piece states
// its own drawing and nothing else.
//
// The module is named for what it holds and not for one of the five: `ink`
// would misname the clocks and the ring helper. It stands beside the pieces
// because a piece is its first reader; `ui/chart/legend.ts` reads the same
// table, because the legend must ink a row exactly as the map inks the mark.
//
// **THE INK COMES FROM THE PACK.** `chart.toml` gives every reading an
// `ink`, and `ink = 0` means "the client's own colour" — the palette text,
// a faction hue, a resource hue, whichever the piece names. The palette
// itself is read out of `PALETTE_TOKENS`, the generated theme table, so no
// colour of the chart is a literal in this tree (R362).

import {
  ACCENT_BRIGHT, ACCENT_COLOR, CHART, FLEET_ANCHOR_GOLD, PALETTE_BAD, PALETTE_TOKENS, TERRAIN_RINGS,
} from '../../../gen/presentation';
import { COL_PITCH, HEX_SIZE } from '../../../gen/balance';
import { ROW_PITCH } from '../../../world';
import { unionOutline } from '../../../render/hex-ring';
import { polylineLength, type Pt } from '../../../render/text-run';
import type { GameState } from '../../../state';
import type { ChartFace } from '../../gfx/atlas';
import { outside, type ViewRect } from '../../layer';
import type { SegmentSink } from '../../layers/batches';
import type { ChartReading } from '../context';
import { packChannels, packHex } from '../../../render/color';
import { dashesOn } from '../../../render/math';
import { pad2 } from '../../../locale/stamp';
import { secOfDay } from '../../../locale/clock';

/** One `#rrggbb` token of the generated palette, as a packed number. */
function tokenInk(name: keyof typeof PALETTE_TOKENS): number {
  return packHex(PALETTE_TOKENS[name]);
}

/**
 * Every weight the cards write in.
 *
 * The mockups call the three greys PALE, DIM and FAINT. They are the
 * published theme tokens, so a banner change moves the chart with the rest
 * of the chrome, and no piece carries a colour literal.
 */
export const CHART_INK = {
  /** The chrome text weight: the ink of nearly every chart label. */
  pale: tokenInk('--text'),
  dim: tokenInk('--dim'),
  faint: tokenInk('--faint'),
  /** The ground the border band fills between its divisions. */
  deep: packChannels(PALETTE_TOKENS['--deep-rgb']),
  /** The chrome accent: the sonar's own cyan (R323). */
  accent: ACCENT_COLOR,
  /** The bright accent, the near-white light of the one mark that matters:
   *  the compass rose's needle to the hold and its bearing (R1745). */
  bright: ACCENT_BRIGHT,
  /** The chart's magenta, `[chart] magenta` of chart.toml (R2868). */
  magenta: CHART.magenta,
  /** The gold of your own anchor cable (§6.1). */
  gold: FLEET_ANCHOR_GOLD,
  /** The brightest of the four ridge contour rings: the cards' `RINGS[3]`. */
  ridge: TERRAIN_RINGS[TERRAIN_RINGS.length - 1].color,
  /** The chrome's bad ink, frozen under every theme: the ring of an inbox
   *  pin of a loss or a hold item (R1846). */
  bad: packHex(PALETTE_BAD),
} as const;

/** The pack's ink for a reading, or the piece's own colour when it is 0. */
export function inkOf(packed: number, own: number): number {
  return packed === 0 ? own : packed;
}

/** The face a reading writes in: the italic Fell, or the mono. */
export function faceOf(reading: ChartReading): ChartFace {
  return reading === 'historical' ? 'fell' : 'mono';
}

/** `hh:00` of one world hour. */
export function onTheHour(hour: number): string {
  return `${pad2(hour)}:00`;
}

/** The whole hour of a world time of day, 0 to 23. The historical reading
 *  writes it as `hh:00` like every other clock (R1544): a time is never a
 *  twelve-hour figure. */
export function hourOfDay(worldSec: number): number {
  return Math.floor(secOfDay(worldSec) / 3600);
}

/**
 * Which day of the season it is, counted from one (card 06 states it).
 *
 * The season is world time (R93), so the elapsed part is the pack's whole
 * length less what the client's own countdown says is left. `aheadSec` asks
 * for the day of a moment that many world seconds from now.
 */
export function seasonDay(state: GameState, aheadSec = 0): number {
  return Math.floor((state.seasonElapsedSec() + aheadSec) / 86400) + 1;
}

/**
 * Does a piece's `show` flag draw every mark, or only the one the pointer
 * or the selection names (R569)? The emitter narrows `show` to the ONE word
 * the table holds today, so the flag is read as a string: the owner writes
 * `always` in the TOML, and no piece changes.
 */
export function showsAlways(show: string): boolean {
  return show === 'always';
}

/** The world pitch of one grid row, `world.ts`'s own, for the pieces that
 *  read their geometry from here. */
export { ROW_PITCH };

/** The centre line of row `row`, where the sheet prints the row figure. */
export function rowY(row: number): number {
  return HEX_SIZE + row * ROW_PITCH;
}

/** The graduation line where column `col` starts (R805). */
export function colLine(col: number): number {
  return col * COL_PITCH;
}

/** Is a mark more than `margin` world px outside the cull rectangle? It is
 *  the one rect test of `render/cull.ts`, on the rect grown by `margin`. */
export function offCull(cull: ViewRect, x: number, y: number, margin = 0): boolean {
  return outside(x, y, cull.x0 - margin, cull.y0 - margin, cull.x1 + margin, cull.y1 + margin);
}

/**
 * The view grown by `slack` of its own width and height on every side: the
 * window a piece packs once and keeps over a pan (R801, R2626).
 */
export function slackRect(v: ViewRect, slack: number): ViewRect {
  const sx = (v.x1 - v.x0) * slack, sy = (v.y1 - v.y0) * slack;
  return { x0: v.x0 - sx, y0: v.y0 - sy, x1: v.x1 + sx, y1: v.y1 + sy };
}

/** Did the view leave the window a piece packed? One compare a side. */
export function leftRect(v: ViewRect, w: ViewRect): boolean {
  return v.x0 < w.x0 || v.y0 < w.y0 || v.x1 > w.x1 || v.y1 > w.y1;
}

/**
 * One closed loop as straight runs, in a `chart.toml` dash pattern. A
 * pattern the pack left empty draws a solid line.
 */
export function dashedLoop(
  seg: SegmentSink, loop: readonly (readonly [number, number])[],
  width: number, color: number, alpha: number, dash: readonly number[],
): void {
  const on = dash[0] ?? 0, off = dash[1] ?? 0;
  for (let i = 0; i < loop.length; i++) {
    const a = loop[i], b = loop[(i + 1) % loop.length];
    seg.line(a[0], a[1], b[0], b[1], width, color, alpha, 0, on, off);
  }
}

/** One straight run of an outline, in world px. */
export interface Stroke { x0: number; y0: number; x1: number; y1: number }

/**
 * Straight runs, each one in a `chart.toml` dash pattern. A pattern the pack
 * left empty draws solid runs.
 */
export function dashedStrokes(
  seg: SegmentSink, strokes: readonly Stroke[],
  width: number, color: number, alpha: number, dash: readonly number[],
): void {
  const on = dash[0] ?? 0, off = dash[1] ?? 0;
  for (const s of strokes) seg.line(s.x0, s.y0, s.x1, s.y1, width, color, alpha, 0, on, off);
}

/** The outline of a set of cells as straight runs: every side of every loop. */
export function outlineStrokes(cells: readonly number[]): Stroke[] {
  const out: Stroke[] = [];
  for (const loop of unionOutline(cells)) {
    for (let i = 0; i < loop.length; i++) {
      const a = loop[i], b = loop[(i + 1) % loop.length];
      out.push({ x0: a[0], y0: a[1], x1: b[0], y1: b[1] });
    }
  }
  return out;
}

/** A traced loop as the points a text run walks. */
export function asPts(loop: readonly (readonly [number, number])[]): Pt[] {
  return loop.map((p) => ({ x: p[0], y: p[1] }));
}

/**
 * The index of the top-most point of a loop, which is its lowest y: the
 * crown a name or a figure centres on. Of two points at one height the
 * first wins.
 */
export function crownOf(loop: readonly (readonly [number, number])[]): number {
  let crown = 0;
  for (let i = 1; i < loop.length; i++) if (loop[i][1] < loop[crown][1]) crown = i;
  return crown;
}

/** The arc length from the first point of a polyline to its point `i`. */
export function lengthTo(pts: readonly (readonly [number, number])[], i: number): number {
  let s = 0;
  for (let k = 0; k < i; k++) s += Math.hypot(pts[k + 1][0] - pts[k][0], pts[k + 1][1] - pts[k][1]);
  return s;
}

/** The longest loop of a traced outline, or null: the one a name belongs on. */
export function longestLoop<L extends readonly (readonly [number, number])[]>(
  loops: readonly L[],
): L | null {
  let best: L | null = null;
  let bestLen = 0;
  for (const loop of loops) {
    const len = polylineLength(asPts(loop), true);
    if (len > bestLen) { best = loop; bestLen = len; }
  }
  return best;
}

/**
 * A dashed ring, from a `chart.toml` dash pattern.
 *
 * The pack states a dash the way a canvas states one — `[on, off]` in px —
 * and the circle batch takes a dash COUNT round the ring and the fraction
 * of each step that is drawn. This converts the one into the other, so a
 * piece writes the pattern it was given and never a dash count.
 *
 * A pattern the pack left empty draws a plain ring.
 */
export function dashedRing(
  circ: { ring: (x: number, y: number, r: number, w: number, c: number, a: number) => void;
    dashed: (x: number, y: number, r: number, w: number, count: number, fill: number,
      c: number, a: number) => void },
  x: number, y: number, r: number, width: number,
  dash: readonly number[], color: number, alpha: number,
): void {
  const on = dash[0] ?? 0, off = dash[1] ?? 0;
  const period = on + off;
  if (!(period > 0) || !(r > 0)) { circ.ring(x, y, r, width, color, alpha); return; }
  const count = dashesOn(r, on, off);
  circ.dashed(x, y, r, width, count, on / period, color, alpha);
}
